/**
 * Client-side validation for auth forms.
 * Matches Identity backend rules for instant feedback.
 */

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(email: string): ValidationResult {
  const errors: string[] = [];
  const trimmed = email.trim();
  if (!trimmed) {
    errors.push('auth_error_email_required');
  } else if (!EMAIL_RE.test(trimmed)) {
    errors.push('auth_error_email_invalid');
  }
  return { valid: errors.length === 0, errors };
}

export function validatePassword(password: string): ValidationResult & { strength: number } {
  const errors: string[] = [];
  let strength = 0;

  if (password.length < 8) {
    errors.push('auth_error_password_min_length');
  } else {
    strength += 1;
  }

  if (/[a-z]/.test(password)) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 1;

  // Strength: 0-1 = weak, 2-3 = medium, 4-5 = strong
  return { valid: errors.length === 0 && strength >= 2, errors, strength };
}

export function validateName(name: string): ValidationResult {
  const errors: string[] = [];
  const trimmed = name.trim();
  if (!trimmed) {
    errors.push('auth_error_name_required');
  } else if (trimmed.length < 2) {
    errors.push('auth_error_name_too_short');
  } else if (trimmed.length > 50) {
    errors.push('auth_error_name_too_long');
  }
  return { valid: errors.length === 0, errors };
}

export function validatePasswordMatch(password: string, confirm: string): ValidationResult {
  const errors: string[] = [];
  if (password !== confirm) {
    errors.push('auth_error_passwords_no_match');
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Validate a guest nickname: 2..48 chars, letters/numbers/spaces/basic punctuation.
 * Allows Unicode letters (incl. Arabic, accents) and common name punctuation.
 */
export function validateNickname(nickname: string): ValidationResult {
  const errors: string[] = [];
  const trimmed = nickname.trim();
  if (trimmed.length < 2) {
    errors.push('auth_guest_error_too_short');
  } else if (trimmed.length > 48) {
    errors.push('auth_guest_error_too_long');
  } else if (!/^[\p{L}\p{N}][\p{L}\p{N}\s'.\-_]*$/u.test(trimmed)) {
    errors.push('auth_guest_error_invalid');
  }
  return { valid: errors.length === 0, errors };
}

/**
 * Get a human-readable strength label key for i18n.
 */
export function getStrengthLabel(strength: number): string {
  if (strength <= 1) return 'auth_password_weak';
  if (strength <= 3) return 'auth_password_medium';
  return 'auth_password_strong';
}

/**
 * Get strength color for UI. These are SEMANTIC colors (red=bad, amber=ok,
 * green=good) — universal UX pattern, kept independent of the AWENI brand
 * palette so users instantly recognize the strength signal.
 */
export function getStrengthColor(strength: number): string {
  if (strength <= 1) return '#EF4444';   // red — weak
  if (strength <= 3) return '#F59E0B';   // amber — ok
  return '#16A34A';                      // green — strong (semantic, not brand emerald)
}
